import { bonusCity, buildingMaxHp, clampHp } from '../bonuses';
import { invalidateDerived } from '../derivedRegistry';
import { GROWTH_RECHECK_TICKS, growAll, growBuilding, releaseOutside } from '../footprint';
import { buildsFor, enterBuilding, roomFor, unitsByMint } from '../occupancy';
import type { SimContext, SimState, SystemFn } from '../types';

const COMPLETION_EPSILON = 1e-9;

const construction: SystemFn = (state: SimState, ctx: SimContext, _inputs, dt: number): void => {
  const { rules } = ctx;

  // Territory may have shrunk since last tick (a new city, a fallen one):
  // footprint tiles now outside it go back to the land.
  releaseOutside(state, ctx);

  // Stale-order cleanup: units with build order whose buildingId no longer exists
  for (const unit of Object.values(state.units)) {
    if (unit.order?.kind === 'build' && !(unit.order.buildingId in state.buildings)) {
      // A pinned builder whose site is gone goes back to automation.
      if (unit.order.pin && rules.units[unit.type]?.automate) unit.auto = {};
      delete unit.order;
    }
  }

  for (const b of Object.values(state.buildings)) {
    if (b.buildProgress >= 1) continue;

    const def = rules.buildings[b.type];
    if (!def) continue;

    if (def.consumesBuilder) {
      // Self-advancing (city): progress without a worker
      b.buildProgress = Math.min(1, b.buildProgress + dt / def.buildTime);
    } else {
      // An idle builder of the owner standing on the site takes the job, so
      // a site whose worker was sent elsewhere can be resumed by moving any
      // worker onto it.
      for (const u of Object.values(state.units)) {
        if (u.owner !== b.owner || !buildsFor(rules, u.type, def.builtBy)) continue;
        if (u.order || u.path || u.pos.q !== b.pos.q || u.pos.r !== b.pos.r) continue;
        u.order = { kind: 'build', buildingId: b.id };
      }
      // Farm: only advances if there is a qualifying worker on-site
      const hasWorker = Object.values(state.units).some(
        (u) =>
          u.order?.kind === 'build' &&
          u.order.buildingId === b.id &&
          u.owner === b.owner &&
          u.pos.q === b.pos.q &&
          u.pos.r === b.pos.r
      );
      if (!hasWorker) continue;
      b.buildProgress = Math.min(1, b.buildProgress + dt / def.buildTime);
    }

    if (b.buildProgress >= 1 - COMPLETION_EPSILON) {
      // A finished barracks raises its city's hp by the bonus it adds (none
      // if the city already has its maxPerCity counted).
      const city = bonusCity(state, rules, b);
      b.buildProgress = 0;
      const cityBefore = city ? buildingMaxHp(state, rules, city) : 0;
      b.buildProgress = 1;
      if (city) city.hp += buildingMaxHp(state, rules, city) - cityBefore;
      // A new city's disc can take a bonus building's tile from another city.
      if (def.territoryRadius !== undefined) clampHp(state, rules);
      if (def.growth) growBuilding(state, ctx, b);
      if (!def.consumesBuilder) {
        // Clear build orders for all workers referencing this building; the
        // builders on it go inside when it takes them (a farm: gatherable this
        // tick), else settle steps them off next tick.
        let inside = Object.values(state.units).filter((u) => u.inside === b.id).length;
        for (const unit of unitsByMint(state)) {
          if (unit.order?.kind !== 'build' || unit.order.buildingId !== b.id) continue;
          // A pinned builder keeps on with the job: working the finished
          // building when it provides a resource, else back to automation.
          const pinned = unit.order.pin;
          if (pinned && def.provides && rules.units[unit.type]?.actions.includes('gather')) {
            unit.order = { kind: 'gather', target: { q: b.pos.q, r: b.pos.r }, pin: true };
          } else {
            delete unit.order;
            if (pinned && rules.units[unit.type]?.automate) unit.auto = {};
          }
          if (unit.path || unit.pos.q !== b.pos.q || unit.pos.r !== b.pos.r) continue;
          if (inside >= roomFor(rules, b, unit)) continue;
          enterBuilding(unit, b);
          inside++;
        }
      }
    }
  }

  // Growing buildings short of their level's extra tiles look again now and
  // then, so land freed later (a razed neighbour) still gets used.
  if (state.tick % GROWTH_RECHECK_TICKS === 0) growAll(state, ctx);
  // Sites progressed or finished, footprints grew or shrank, builders went inside.
  invalidateDerived(state);
};

export { construction };
